import { TypedEventTarget } from '@chronosphereio/core';
import { v4 as uuidv4 } from 'uuid';

/**
 * The pinned time range status for a tab. The `unknown` status is a temporary status used in new tabs while the real
 * status is being determined asynchronously.
 */
export type PinnedStatus = 'pinned' | 'unpinned' | 'pinned-in-another-tab' | 'unknown';

/**
 * A class that keeps track of the current `PinnedStatus` for the time range in a tab.
 */
export class PinnedStatusManager {
  private readonly broadcastChannel: BroadcastChannel = new BroadcastChannel('CHRONOSPHERE_PINNED_TIME');
  private readonly eventTarget: TypedEventTarget<PinnedStatusEvents> = new TypedEventTarget();
  private otherPinnedTabsCount: number;
  private status: PinnedStatus;

  constructor() {
    const isEnabled = this.getIsPinnedFromStorage();
    if (isEnabled === true) {
      this.status = 'pinned';
    } else if (isEnabled === false) {
      this.status = 'unpinned';
    } else {
      this.status = 'unknown';
    }

    this.otherPinnedTabsCount = 0;
    this.broadcastChannel.addEventListener('message', this.handleMessage);
    void this.refresh();

    // See: https://developer.chrome.com/docs/web-platform/page-lifecycle-api for details on these events
    document.addEventListener('visibilitychange', this.handleVisibilityChange, { capture: true });
    window.addEventListener('pagehide', this.handlePagehide, { capture: true });
  }

  /**
   * Gets the current `PinnedStatus` for the tab.
   */
  getPinnedStatus = (): PinnedStatus => {
    return this.status;
  };

  /**
   * Transition to `pinned` status when the user pins a time range.
   */
  pin = (): void => {
    if (this.status === 'pinned') {
      return;
    }

    this.setIsPinnedInStorage(true);
    this.setStatusAndDispatch('pinned');
    this.postPinnedTimeMessage({ kind: 'pinned-state-change', isPinned: true });
  };

  /**
   * Transition to `unpinned` or `pinned-in-another-tab` status when the user unpins the time range.
   */
  unpin = (): void => {
    if (this.status !== 'pinned') {
      return;
    }

    this.setIsPinnedInStorage(false);
    if (this.otherPinnedTabsCount > 0) {
      this.setStatusAndDispatch('pinned-in-another-tab');
    } else {
      this.setStatusAndDispatch('unpinned');
    }
    this.postPinnedTimeMessage({ kind: 'pinned-state-change', isPinned: false });
  };

  /**
   * Listen to changes for the current `PinnedStatus`.
   */
  subscribeToPinnedStatus = (callback: () => void): (() => void) => {
    this.eventTarget.addEventListener('change', callback);
    return () => {
      this.eventTarget.removeEventListener('change', callback);
    };
  };

  /**
   * Force a refresh of the current `PinnedStatus` to make sure it's up to date. Will ping any other open tabs to try
   * and ensure that `unpinned` or `pinned-in-another-tab` statuses are accurate. The Promise that's returned resolves
   * with the latest status.
   */
  refresh = async (): Promise<PinnedStatus> => {
    // Always kick off a ping to refresh the other pinned tab count
    const refreshCountPromise = this.refreshPinnedTabCount();

    // If we're already pinned, the status isn't going to change due to the tab count, so just bail
    if (this.status === 'pinned') {
      return this.status;
    }

    // For all other statuses, the tab count matters so wait for it to finish
    await refreshCountPromise;

    // Since we're in a new tab, start pinned-in-another-tab only if any other tabs are pinned
    if (this.status === 'unknown') {
      const isPinnedInAnotherTab = this.otherPinnedTabsCount > 0;
      const newStatus = isPinnedInAnotherTab ? 'pinned-in-another-tab' : 'unpinned';
      this.setIsPinnedInStorage(false);
      this.setStatusAndDispatch(newStatus);
      return this.status;
    }

    // If we currently think we're pinned in another tab, but we're actually not any more...
    if (this.status === 'pinned-in-another-tab' && this.otherPinnedTabsCount === 0) {
      this.setStatusAndDispatch('unpinned');
      return this.status;
    }

    // If we currently think we're unpinned, but there are actually other tabs pinned...
    if (this.status === 'unpinned' && this.otherPinnedTabsCount > 0) {
      this.setStatusAndDispatch('pinned-in-another-tab');
      return this.status;
    }

    return this.status;
  };

  /**
   * Cleans-up event listeners and the `BroadcastChannel`. Mostly just here for use in tests since normally we have a
   * singleton instance of this class per tab that will live until the user closes the tab.
   */
  dispose(): void {
    this.broadcastChannel.removeEventListener('message', this.handleMessage);
    this.broadcastChannel.close();

    document.removeEventListener('visibilitychange', this.handleVisibilityChange, { capture: true });
    window.removeEventListener('pagehide', this.handlePagehide, { capture: true });
  }

  private async refreshPinnedTabCount() {
    const pingId = uuidv4();

    let newTabCount = 0;
    const pongHandler = (e: MessageEvent) => {
      const message = e.data;
      if (isPinnedTimeMessage(message) && message.kind === 'pong-pinned' && message.pingId === pingId) {
        newTabCount++;
      }
    };

    this.broadcastChannel.addEventListener('message', pongHandler);
    this.postPinnedTimeMessage({ kind: 'ping-pinned', pingId });

    // Wait for responses with a timeout since there might not be anyone to respond (TODO: It's unclear to me whether
    // this is necessary or whether posting messages is sync and so all message handlers will run before we get here)
    await new Promise((resolve) => window.setTimeout(resolve, 500));
    this.broadcastChannel.removeEventListener('message', pongHandler);
    this.otherPinnedTabsCount = newTabCount;
  }

  private setStatusAndDispatch(next: PinnedStatus) {
    this.status = next;
    this.eventTarget.dispatchCustomEvent('change', {});
  }

  private getIsPinnedFromStorage(): boolean | undefined {
    try {
      const serialized = window.sessionStorage.getItem(IS_PINNED_ENABLED_STORAGE_KEY);
      if (serialized === null) {
        return undefined;
      }
      return JSON.parse(serialized);
    } catch {
      return undefined;
    }
  }

  private setIsPinnedInStorage(value: boolean): void {
    window.sessionStorage.setItem(IS_PINNED_ENABLED_STORAGE_KEY, JSON.stringify(value));
  }

  private postPinnedTimeMessage(message: PinnedTimeMessage) {
    this.broadcastChannel.postMessage(message);
  }

  private handleMessage = (event: MessageEvent) => {
    const message = event.data;
    if (isPinnedTimeMessage(message) === false) {
      // TODO: Should we log an error / warning here since this shouldn't ever happen?
      return;
    }

    switch (message.kind) {
      case 'ping-pinned':
        // Respond to pings from other tabs with a pong if we're pinned
        if (this.status === 'pinned') {
          this.postPinnedTimeMessage({ kind: 'pong-pinned', pingId: message.pingId });
        }
        return;
      case 'pinned-state-change':
        // Update the tab count based on whether the other tab is pinned now or not
        if (message.isPinned) {
          this.otherPinnedTabsCount++;
        } else if (this.otherPinnedTabsCount > 0) {
          this.otherPinnedTabsCount--;
        }

        // If we're no longer pinned in another tab...
        if (this.status === 'pinned-in-another-tab' && this.otherPinnedTabsCount === 0) {
          this.setStatusAndDispatch('unpinned');
          return;
        }

        // If we're now pinned in another tab...
        if (this.status === 'unpinned' && this.otherPinnedTabsCount > 0) {
          this.setStatusAndDispatch('pinned-in-another-tab');
          return;
        }

        return;
    }
  };

  private handleVisibilityChange = (_event: Event) => {
    if (document.visibilityState === 'hidden') {
      return;
    }

    // When a user switches back to a tab after it's been hidden, run a refresh
    void this.refresh();
  };

  private handlePagehide = (event: PageTransitionEvent) => {
    if (event.persisted) {
      return;
    }

    // When event.persisted is false, that means the user closed the tab and it's about to be terminated, so if we're
    // currently pinned, let the other tabs know that we're not any more because we're being closed
    if (this.status === 'pinned') {
      this.postPinnedTimeMessage({ kind: 'pinned-state-change', isPinned: false });
    }
  };
}

interface PinnedStatusEvents {
  change: CustomEvent<ChangeDetail>;
}

interface ChangeDetail {
  // Nothing needed currently
}

interface PingPinnedMessage {
  kind: 'ping-pinned';
  pingId: string;
}

interface PongPinnedMessage {
  kind: 'pong-pinned';
  pingId: string;
}

interface PinnedStateChange {
  kind: 'pinned-state-change';
  isPinned: boolean;
}

type PinnedTimeMessage = PingPinnedMessage | PongPinnedMessage | PinnedStateChange;

function isPinnedTimeMessage(message: unknown): message is PinnedTimeMessage {
  return typeof message === 'object' && message !== null && 'kind' in message && typeof message.kind === 'string';
}

// Only exported for use in tests
export const IS_PINNED_ENABLED_STORAGE_KEY = 'CHRONOSPHERE_PINNED_TIME_RANGE_ENABLED';
